import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React from "react";
import { Box, Button, Container, Flex, Heading, Image, Link, SimpleGrid, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normalizeSiteLanguage } from "../i18n/language";
import SEO from "./SEO";
import Footer from "./Footer";

const featureImages = [
  { key: "lifeMoments", folder: "life-moments", file: "life_moments" },
  { key: "aiWritingAssistant", folder: "ai-writing-assistant", file: "ai_writing_assistant" },
  { key: "lifeTrajectories", folder: "life-trajectories", file: "life_trajectories" },
] as const;

const DiaryApp: React.FC = () => {
  const { t, i18n } = useTranslation();
  const localizePath = useLocalizedPath();
  const language = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const imageLanguage = language === "en" ? "en" : "zh_hk";
  const downloadUrl = process.env.REACT_APP_U_LINK || "https://deepsoulai.onelink.me/bf43/downloadAppDC";
  const downloadButton = (
    <Button as="a" href={downloadUrl} colorScheme="primary" size="lg"
      whiteSpace="normal" height="auto" minH="48px" py={3}>
      {t("diaryPage.download")}
    </Button>
  );

  return (
    <>
      <SEO title={t("diaryPage.seoTitle")} description={t("diaryPage.seoDescription")}
        canonical="https://deepsoulai.com/diary-app" />
      <Box as="main" color="gray.800" pt={{ base: 28, md: 36 }} pb={{ base: 12, md: 20 }}>
        <Container maxW="1200px" px={{ base: 5, md: 8 }}>
          <Box as="section" bgGradient="linear(to-br, pink.50, white, purple.50)"
            border="1px solid" borderColor="blackAlpha.100" borderRadius="3xl"
            p={{ base: 7, md: 14 }} mb={{ base: 12, md: 20 }}>
            <Text fontWeight="700" color="primary.700" mb={5}>{t("diaryPage.eyebrow")}</Text>
            <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.25" maxW="900px">
              {t("diaryPage.title")}
            </Heading>
            <Text mt={6} fontSize={{ base: "md", md: "xl" }} lineHeight="1.9" maxW="780px" color="gray.700">
              {t("diaryPage.description")}
            </Text>
            <Flex mt={8} gap={4} wrap="wrap" align="center">
              {downloadButton}
              <Button as="a" href="#diary-steps" variant="outline" colorScheme="primary" size="lg">
                {t("diaryPage.seeSteps")}
              </Button>
            </Flex>
            <Text mt={4} fontSize="sm" color="gray.600">{t("diaryPage.platforms")}</Text>
          </Box>

          <Box as="section" aria-labelledby="diary-features-title" mb={{ base: 12, md: 20 }}>
            <Heading as="h2" id="diary-features-title" fontSize={{ base: "2xl", md: "3xl" }}>
              {t("diaryPage.featuresTitle")}
            </Heading>
            <Text mt={4} mb={8} maxW="760px" lineHeight="1.9">{t("diaryPage.featuresIntro")}</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {featureImages.map(({ key, folder, file }) => (
                <Box as="article" key={key} bg="white" border="1px solid"
                  borderColor="blackAlpha.200" borderRadius="2xl" overflow="hidden">
                  <Box p={6}>
                    <Heading as="h3" fontSize="xl" mb={3}>{t(`v2Home.why.items.${key}.title`)}</Heading>
                    <Text lineHeight="1.8" color="gray.700">{t(`v2Home.why.items.${key}.description`)}</Text>
                  </Box>
                  <Image
                    src={`https://assets.deepsoulai.com/main-page/product-value/${folder}/${file}_01_${imageLanguage}.png`}
                    alt={t(`v2Home.why.items.${key}.galleryImageAlt1`)}
                    loading="lazy" w="100%" maxW="360px" mx="auto" h="auto"
                  />
                </Box>
              ))}
            </SimpleGrid>
            <Text mt={4} fontSize="sm" color="gray.600">{t("diaryPage.galleryNote")}</Text>
          </Box>

          <Box as="section" aria-labelledby="diary-example-title" bg="purple.50"
            borderRadius="3xl" p={{ base: 6, md: 10 }} mb={{ base: 12, md: 20 }}>
            <Heading as="h2" id="diary-example-title" fontSize={{ base: "2xl", md: "3xl" }}>
              {t("diaryPage.exampleTitle")}
            </Heading>
            <Text mt={4} mb={6} color="gray.600" lineHeight="1.8">{t("diaryPage.exampleNote")}</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {(["before", "after"] as const).map((stage) => (
                <Box key={stage} bg="white" borderRadius="2xl" p={{ base: 5, md: 7 }}>
                  <Heading as="h3" fontSize="lg" mb={4}>{t(`diaryPage.${stage}Label`)}</Heading>
                  <Text lineHeight="1.9">{t(`diaryPage.${stage}`)}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Text mt={5} fontSize="sm" lineHeight="1.8" color="gray.700">{t("diaryPage.reviewNote")}</Text>
          </Box>

          <Box as="section" id="diary-steps" aria-labelledby="diary-steps-title"
            scrollMarginTop="110px" mb={{ base: 12, md: 20 }}>
            <Heading as="h2" id="diary-steps-title" fontSize={{ base: "2xl", md: "3xl" }} mb={8}>
              {t("diaryPage.stepsTitle")}
            </Heading>
            <SimpleGrid as="ol" columns={{ base: 1, md: 3 }} spacing={6} m={0} listStyleType="none">
              {[0, 1, 2].map((index) => (
                <Box as="li" key={index} p={6} bg="white" borderRadius="2xl"
                  border="1px solid" borderColor="blackAlpha.200">
                  <Text aria-hidden="true" fontSize="3xl" fontWeight="700" color="primary.700" mb={3}>
                    {String(index + 1).padStart(2, "0")}
                  </Text>
                  <Heading as="h3" fontSize="xl" mb={3}>{t(`diaryPage.steps.${index}.title`)}</Heading>
                  <Text color="gray.700" lineHeight="1.9">{t(`diaryPage.steps.${index}.text`)}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Box as="section" textAlign="center" bg="pink.50" borderRadius="3xl" p={{ base: 7, md: 12 }}>
            <Heading as="h2" fontSize={{ base: "2xl", md: "3xl" }}>{t("diaryPage.closingTitle")}</Heading>
            <Text mt={4} mb={7} lineHeight="1.9">{t("diaryPage.closingText")}</Text>
            {downloadButton}
            <Box mt={6}>
              <Link as={RouterLink} to={localizePath("/#faq")} color="primary.700" textDecoration="underline">
                {t("diaryPage.faqLink")}
              </Link>
            </Box>
            <Box mt={4}>
              <Link as={RouterLink} to={localizePath("/")} color="primary.700" textDecoration="underline">
                {t("diaryPage.homeLink")}
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default DiaryApp;

